import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { useSelector } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { CMS_HUB_PROFESSIONAL, SALES_HUB_PROFESSIONAL, SERVICE_HUB_PROFESSIONAL, OPERATIONS_HUB_PROFESSIONAL } from 'self-service-api/constants/ApiNames';
import { CMS, SALES, SERVICE, OPERATIONS } from 'self-service-api/constants/ProductLines';
import UILink from 'UIComponents/link/UILink';
import { promotionKeys } from 'self-service-api/constants/Promotions';
import { getHSFSPurchaseUrl, getLatamEsPromotionUrl } from 'pricing-pages-lib/utils/urls';
import { allEligibleDiscountsSelector, promotionForSkuIdSelector } from 'pricing-pages-ui/selectors/PromotionSelectors';
import { hasPricingAndPurchasingSucceededSelector } from '../../selectors/LoadingStateSelectors';
import { currentSkuIdForProductSelector } from '../../selectors/ProductsSelectors';
import PromotionBanner from './PromotionBanner';

const PromotionBannerContainer = ({
  productLine
}) => {
  const hasPricingAndPurchasingSucceeded = useSelector(hasPricingAndPurchasingSucceededSelector);
  const eligibleDiscounts = useSelector(allEligibleDiscountsSelector);
  const getProductPromotionEligibilityMap = useSelector(state => {
    return apiName => promotionForSkuIdSelector(state, {
      skuId: currentSkuIdForProductSelector(state, {
        apiName
      })
    });
  }); // Returns a getter function that retrieves the copy keys for LATAM-ES

  const getRegionalHubPromotionCopyKeyGetter = apiName => {
    const copyKeyMap = {
      [SALES_HUB_PROFESSIONAL]: 'salesOnlyProfessional',
      [SERVICE_HUB_PROFESSIONAL]: 'serviceOnlyProfessional',
      [CMS_HUB_PROFESSIONAL]: 'cmsHub',
      [OPERATIONS_HUB_PROFESSIONAL]: 'operationsOnlyProfessional'
    };
    const copyKey = copyKeyMap[apiName];
    return promotionKey => {
      const professionalPromotionEligibilityMap = getProductPromotionEligibilityMap(apiName);

      if (professionalPromotionEligibilityMap && professionalPromotionEligibilityMap.eligiblePromotion === promotionKey) {
        return copyKey;
      }

      return null;
    };
  };

  const regionalPromotionCopyKeyGetter = {
    [SALES]: getRegionalHubPromotionCopyKeyGetter(SALES_HUB_PROFESSIONAL),
    [SERVICE]: getRegionalHubPromotionCopyKeyGetter(SERVICE_HUB_PROFESSIONAL),
    [CMS]: getRegionalHubPromotionCopyKeyGetter(CMS_HUB_PROFESSIONAL),
    [OPERATIONS]: getRegionalHubPromotionCopyKeyGetter(OPERATIONS_HUB_PROFESSIONAL)
  }[productLine];

  const isEligibleForPromotion = promotionKey => eligibleDiscounts.includes(promotionKey);

  if (!hasPricingAndPurchasingSucceeded) return null;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [regionalPromotionCopyKeyGetter && /*#__PURE__*/_jsx(PromotionBanner, {
      shouldShow: Boolean(regionalPromotionCopyKeyGetter(promotionKeys.LATAM_AND_BRAZIL_ES_PRO)),
      promotion: promotionKeys.LATAM_AND_BRAZIL_ES_PRO,
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `pricingPage.promotions.latamEs.${regionalPromotionCopyKeyGetter(promotionKeys.LATAM_AND_BRAZIL_ES_PRO)}.title`
      }),
      description: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: `pricingPage.promotions.latamEs.${regionalPromotionCopyKeyGetter(promotionKeys.LATAM_AND_BRAZIL_ES_PRO)}.body_jsx`,
        elements: {
          UILink
        },
        options: {
          url: getLatamEsPromotionUrl()
        }
      }),
      dataSelenium: `${promotionKeys.LATAM_AND_BRAZIL_ES_PRO.toLowerCase()}-promotion-header`
    }), /*#__PURE__*/_jsx(PromotionBanner, {
      shouldShow: isEligibleForPromotion(promotionKeys.HSFS_SEED),
      promotion: promotionKeys.HSFS_SEED,
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `pricingPage.promotions.hsfx.seed.title`
      }),
      description: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: `pricingPage.promotions.hsfx.seed.body_jsx`,
        elements: {}
      }),
      cta: {
        url: getHSFSPurchaseUrl(),
        message: 'pricingPage.promotions.hsfx.cta'
      }
    }), /*#__PURE__*/_jsx(PromotionBanner, {
      shouldShow: isEligibleForPromotion(promotionKeys.HSFX_ENTREPRENEUR),
      promotion: promotionKeys.HSFX_ENTREPRENEUR,
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `pricingPage.promotions.hsfx.entrepreneur.title`
      }),
      description: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: `pricingPage.promotions.hsfx.entrepreneur.body_jsx`,
        elements: {}
      }),
      cta: {
        url: getHSFSPurchaseUrl(),
        message: 'pricingPage.promotions.hsfx.cta'
      }
    }), /*#__PURE__*/_jsx(PromotionBanner, {
      shouldShow: isEligibleForPromotion(promotionKeys.HSFX_BOOTSTRAP),
      promotion: promotionKeys.HSFX_BOOTSTRAP,
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `pricingPage.promotions.hsfx.bootstrap.title`
      }),
      description: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: `pricingPage.promotions.hsfx.bootstrap.body_jsx`,
        elements: {}
      }),
      cta: {
        url: getHSFSPurchaseUrl(),
        message: 'pricingPage.promotions.hsfx.cta'
      }
    }), /*#__PURE__*/_jsx(PromotionBanner, {
      shouldShow: isEligibleForPromotion(promotionKeys.STRATEGIC_PARTNERSHIP),
      promotion: promotionKeys.STRATEGIC_PARTNERSHIP,
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "pricingPage.promotions.hsfx.strategicPartner.title"
      }),
      description: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "pricingPage.promotions.hsfx.strategicPartner.body"
      }),
      cta: {
        url: getHSFSPurchaseUrl(),
        message: 'pricingPage.promotions.hsfx.cta'
      }
    }), /*#__PURE__*/_jsx(PromotionBanner, {
      shouldShow: isEligibleForPromotion(promotionKeys.HSFS_SERIES_A),
      promotion: promotionKeys.HSFS_SERIES_A,
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `pricingPage.promotions.hsfx.series-a.title`
      }),
      description: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: `pricingPage.promotions.hsfx.series-a.body_jsx`,
        elements: {}
      }),
      cta: {
        url: getHSFSPurchaseUrl(),
        message: 'pricingPage.promotions.hsfx.cta'
      }
    })]
  });
};

export default PromotionBannerContainer;